import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiPlay, FiPause, FiDownload, FiX } from 'react-icons/fi';
import { AiAudioGenerationApi } from '../../../services/ai/AiAudioGenerationApi';
import { useToast } from '../../../contexts/ToastContext';
import './TextToSpeechTool.css';
import '../../primitives/Button.css';

const MAX_CHARS = 50000;

/**
 * TextToSpeechTool - Paste any text, choose a voice, and listen to it in the browser.
 *
 * The request to the AI service stays open until the audio is synthesized; the
 * audio is returned inline as an MP3 blob and played via the native <audio>
 * element. Nothing is persisted server-side.
 */
export default function TextToSpeechTool({
  onClose,
  persistedState,
  onStateChange,
}) {
  const toast = useToast?.();

  const [text, setText] = useState(() => persistedState?.text || '');
  const [voice, setVoice] = useState(() => persistedState?.voice || 'female');
  const [isGenerating, setIsGenerating] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const audioRef = useRef(null);
  const audioUrlRef = useRef(null);
  const abortRef = useRef(null);
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;

  // Persist text + voice between tool switches.
  useEffect(() => {
    onStateChangeRef.current?.({ text, voice });
  }, [text, voice]);

  // Revoke object URL on unmount or replacement.
  useEffect(() => {
    return () => {
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  const replaceAudioUrl = useCallback((nextUrl) => {
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
    }
    audioUrlRef.current = nextUrl;
    setAudioUrl(nextUrl);
  }, []);

  const handleGenerate = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setErrorMessage('Please paste some text to read.');
      return;
    }
    if (trimmed.length > MAX_CHARS) {
      setErrorMessage(`Text is too long. Maximum is ${MAX_CHARS.toLocaleString()} characters.`);
      return;
    }

    setErrorMessage(null);
    setIsGenerating(true);
    setIsPlaying(false);
    replaceAudioUrl(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const blob = await AiAudioGenerationApi.textToSpeech({
        text: trimmed,
        voice,
        signal: controller.signal,
      });
      // eslint-disable-next-line no-console
      console.log('[TTS] received blob', { size: blob.size, type: blob.type });
      if (!blob || blob.size === 0) {
        throw new Error('Server returned empty audio.');
      }
      const url = URL.createObjectURL(blob);
      replaceAudioUrl(url);

      // Auto-play once ready.
      requestAnimationFrame(() => {
        if (audioRef.current) {
          audioRef.current.play().catch(() => {
            // Autoplay may be blocked; user can press play.
          });
        }
      });
    } catch (err) {
      if (err?.name === 'AbortError') {
        return;
      }
      const message = err?.message || 'Failed to generate audio.';
      setErrorMessage(message);
      if (toast?.error) {
        toast.error(message);
      }
    } finally {
      setIsGenerating(false);
      abortRef.current = null;
    }
  }, [text, voice, replaceAudioUrl, toast]);

  const handleCancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  const handleClear = useCallback(() => {
    setText('');
    setErrorMessage(null);
    replaceAudioUrl(null);
    setIsPlaying(false);
  }, [replaceAudioUrl]);

  const handleDownload = useCallback(() => {
    if (!audioUrl) return;
    const link = document.createElement('a');
    link.href = audioUrl;
    link.download = `tts-${Date.now()}.mp3`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [audioUrl]);

  const charCount = text.length;
  const overLimit = charCount > MAX_CHARS;

  return (
    <div className="tts-tool" role="region" aria-label="Text to speech">
      <div className="tts-tool__header">
        <div className="tts-tool__title-group">
          <span className="tts-tool__icon" aria-hidden="true">🔊</span>
          <div>
            <h2 className="tts-tool__title">Text to Speech</h2>
            <p className="tts-tool__subtitle">
              Paste any text, choose a voice, and listen in your browser.
            </p>
          </div>
        </div>
        {onClose && (
          <button
            type="button"
            className="ui-btn ui-btn--ghost ui-btn--icon-circular tts-tool__close"
            onClick={onClose}
            aria-label="Close text to speech"
            title="Close"
          >
            <FiX />
          </button>
        )}
      </div>

      <div className="tts-tool__body">
        <label htmlFor="tts-text-input" className="tts-tool__label">
          Text
        </label>
        <textarea
          id="tts-text-input"
          className="tts-tool__textarea"
          placeholder="Paste or type the text you want read aloud..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          disabled={isGenerating}
        />
        <div className={`tts-tool__char-count${overLimit ? ' is-over' : ''}`}>
          {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()} characters
        </div>

        <fieldset className="tts-tool__voice-group" disabled={isGenerating}>
          <legend className="tts-tool__label">Voice</legend>
          <label className={`tts-tool__voice-option${voice === 'female' ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="tts-voice"
              value="female"
              checked={voice === 'female'}
              onChange={() => setVoice('female')}
            />
            <span className="tts-tool__voice-emoji" aria-hidden="true">👩</span>
            <span className="tts-tool__voice-label">Female</span>
          </label>
          <label className={`tts-tool__voice-option${voice === 'male' ? ' is-selected' : ''}`}>
            <input
              type="radio"
              name="tts-voice"
              value="male"
              checked={voice === 'male'}
              onChange={() => setVoice('male')}
            />
            <span className="tts-tool__voice-emoji" aria-hidden="true">👨</span>
            <span className="tts-tool__voice-label">Male</span>
          </label>
        </fieldset>

        {errorMessage && (
          <div className="tts-tool__error" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="tts-tool__actions">
          {!isGenerating ? (
            <button
              type="button"
              className="ui-btn ui-btn--primary"
              onClick={handleGenerate}
              disabled={!text.trim() || overLimit}
            >
              <FiPlay aria-hidden="true" /> Generate &amp; Play
            </button>
          ) : (
            <button
              type="button"
              className="ui-btn ui-btn--neutral"
              onClick={handleCancel}
            >
              <FiPause aria-hidden="true" /> Cancel
            </button>
          )}
          <button
            type="button"
            className="ui-btn ui-btn--ghost"
            onClick={handleClear}
            disabled={isGenerating || (!text && !audioUrl)}
          >
            Clear
          </button>
          {audioUrl && (
            <button
              type="button"
              className="ui-btn ui-btn--ghost"
              onClick={handleDownload}
            >
              <FiDownload aria-hidden="true" /> Download MP3
            </button>
          )}
        </div>

        {isGenerating && (
          <div className="tts-tool__status" aria-live="polite">
            Generating audio... this may take a few seconds.
          </div>
        )}

        {audioUrl && (
          <div className="tts-tool__player">
            <audio
              ref={audioRef}
              src={audioUrl}
              controls
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={() => setIsPlaying(false)}
            >
              <track kind="captions" />
            </audio>
            <span className="tts-tool__player-status">
              {isPlaying ? 'Playing' : 'Ready'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
